// The console's server state, as TanStack Query hooks (spec/03 §3). One key per resource, here, so
// a save can invalidate exactly what it changed.

import { useQuery } from '@tanstack/react-query';

import { useApi } from './client';
import { fetchMyAccess, fetchMyOrganizations, fetchMyVenues, fetchPlatformAccess, type AccessScope } from './me';

export const queryKeys = {
  session: ['session'] as const,
  myVenues: ['me', 'venues'] as const,
  myOrganizations: ['me', 'organizations'] as const,
  access: (scope: AccessScope) => ['me', 'access', scope.venueId ?? null, scope.organizationId ?? null] as const,
  platformAccess: ['me', 'access', 'platform'] as const,
  venue: (organizationId: string, venueId: string) => ['venue', organizationId, venueId] as const,
  venueSettings: (organizationId: string, venueId: string) => ['settings', 'venue', organizationId, venueId] as const,
  organizationSettings: (organizationId: string) => ['settings', 'organization', organizationId] as const,
  platformSettings: ['settings', 'platform'] as const,
  hqOrganizationSettings: (organizationId: string) => ['settings', 'hq-organization', organizationId] as const,
  hqBrand: ['hq', 'brand'] as const,
  /** Every page of one platform player search, and one player's platform record (spec/14 §10). */
  hqPlayerSearches: ['hq', 'players', 'search'] as const,
  hqPlayerSearch: (q: string) => ['hq', 'players', 'search', q] as const,
  hqPlayer: (playerId: string) => ['hq', 'players', 'one', playerId] as const,
  /** One read of the platform audit log: its reason is part of it, because the reason is recorded. */
  hqAudit: (reason: string, filter: Readonly<Record<string, string>>) => ['hq', 'audit', reason, filter] as const,
  /** One read of an organisation's own audit log, keyed by the filter it was read with (spec/13 §15). */
  organizationAudit: (organizationId: string, filter: Readonly<Record<string, string>>) => ['organization', organizationId, 'audit', filter] as const,
  meta: ['meta'] as const,
};

/** The better-auth session, or `null` when signed out. */
export function useSession() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.session, queryFn: () => api.getSession(), staleTime: 60_000 });
}

export function useMyVenues(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.myVenues, queryFn: () => fetchMyVenues(api), enabled });
}

export function useMyOrganizations(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.myOrganizations, queryFn: () => fetchMyOrganizations(api), enabled });
}

export interface Access {
  /** `ready` once the permission set is known; `unavailable` if it could not be read. */
  readonly status: 'loading' | 'ready' | 'unavailable';
  /**
   * Whether to *offer* an action. Fails closed: while loading, and when the set could not be read,
   * nothing is offered. The server checks every request again (spec/13 §1) — this only decides what
   * is shown.
   */
  readonly can: (permission: string) => boolean;
  /** The caller's membership at this scope (`mem_…`), once known — to tell their own records from others'. */
  readonly memberId: string | null;
  /** The caller's role at this scope (`owner`, `manager`, …), once known. */
  readonly role?: string | null;
  /**
   * Which Clavian products this organisation has (`MyAccess.products`): Clavian Stations (sessions, players, selling time)
   * and Clavian Boot. Until it is known, or when the api does not say: Stations only, as every venue was before Boot.
   */
  readonly products: Products;
}

export interface Products {
  readonly stations: boolean;
  readonly boot: boolean;
}

const STATIONS_ONLY: Products = { stations: true, boot: false };

/**
 * The signed-in person's platform role and its permissions (`GET /v1/me/access` with no scope): what
 * the HQ console shows and hides by (spec/14 §1). Read past the cookie cache, as the server reads it.
 */
export function usePlatformAccess() {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.platformAccess, queryFn: () => fetchPlatformAccess(api), retry: false });
}

export function useAccess(scope: AccessScope | undefined): Access {
  const api = useApi();
  const query = useQuery({
    queryKey: scope === undefined ? ['me', 'access', 'none'] : queryKeys.access(scope),
    queryFn: () => fetchMyAccess(api, scope as AccessScope),
    enabled: scope !== undefined,
    retry: false,
  });
  const permissions = query.data?.permissions;
  return {
    status: query.isError ? 'unavailable' : permissions === undefined ? 'loading' : 'ready',
    can: (permission) => permissions?.has(permission) ?? false,
    memberId: query.data?.memberId ?? null,
    role: query.data?.role ?? null,
    products: query.data?.products ?? STATIONS_ONLY,
  };
}
